import { getChain, getCurrency } from "@oxygen/concepts";
import type { FC } from "react";

import Icon from "src/components/icon/icon";
import { splitTicker } from "src/utils/format";

interface CryptoIconProps {
	ticker: string;
	className?: string;
	showLabel?: boolean;
	chainBadge?: string;
	showComposite?: boolean;
}

const resolveIconName = (symbol: string): string =>
	getCurrency(symbol)?.iconName ||
	getChain(symbol)?.iconName ||
	symbol.toLowerCase();

const getCryptoIconName = (ticker: string): string => {
	const { token, blockchain } = splitTicker(ticker);
	return resolveIconName(token || blockchain);
};

export const CryptoIcon: FC<CryptoIconProps> = ({
	ticker,
	className,
	showLabel = false,
	showComposite = false,
}) => {
	const { token, blockchain } = splitTicker(ticker);

	const iconName =
		showComposite && token ? resolveIconName(token) : getCryptoIconName(ticker);

	const shouldShowChainBadge = showComposite && token && blockchain;
	const badgeIconName = blockchain ? resolveIconName(blockchain) : null;

	if (showLabel) {
		return (
			<div className="flex items-center gap-2">
				<div className="relative inline-block w-5">
					<Icon name={iconName} dir="crypto" className={className} />
					{shouldShowChainBadge && badgeIconName && (
						<div className="absolute -bottom-0.5 -right-0.5 rounded-full bg-background ring-[0.5px] p-[1px] ring-border/60">
							<Icon
								name={badgeIconName}
								dir="crypto"
								className={
									className?.includes("h-6") || className?.includes("w-6")
										? "h-3 w-3"
										: "h-2.5 w-2.5"
								}
							/>
						</div>
					)}
				</div>
				<span>{ticker}</span>
			</div>
		);
	}

	if (shouldShowChainBadge && badgeIconName) {
		return (
			<div className="relative inline-block">
				<Icon name={iconName} dir="crypto" className={className} />
				<div className="absolute -bottom-0.5 -right-0.5 rounded-full bg-background p-[1px] ring-[0.5px] ring-border/60">
					<Icon
						name={badgeIconName}
						dir="crypto"
						className={
							className?.includes("h-6") || className?.includes("w-6")
								? "h-3 w-3"
								: "h-2.5 w-2.5"
						}
					/>
				</div>
			</div>
		);
	}

	return <Icon name={iconName} dir="crypto" className={className} />;
};

export { getCryptoIconName };
